import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  useSplit,
  useSplitEnabled,
  useSplitEnabledWithConfig,
} from '@bufferapp/features';

import FoldersIcon from '@bufferapp/ui/Icon/Icons/Folders';
import CalendarIcon from '@bufferapp/ui/Icon/Icons/Calendar';
import ChartLineIcon from '@bufferapp/ui/Icon/Icons/ChartLine';
import CopyIcon from '@bufferapp/ui/Icon/Icons/Copy';
import CaretDown from '@bufferapp/ui/Icon/Icons/CaretDown';
import CaretUp from '@bufferapp/ui/Icon/Icons/CaretUp';
import MagicWandIcon from '@bufferapp/ui/Icon/Icons/MagicWand';
import { Divider } from '@bufferapp/components';

import { useAppSelector } from '~/store';
import { selectHasContentFeature } from '~/organizations/selectors';
import { useAccount } from '~/accountContext';
import { PaywallTag, NewTag } from './sidebarButtonTags';

import { SidebarListItem, ButtonWrapper, BagdesWrapper } from './styles';

const SidebarButtons = ({
  onContentButtonClick,
  isContentSelected,
  onCalendarButtonClick,
  isCalendarSelected,
  onCampaignsButtonClick,
  onBlueprintsButtonClick,
  isCampaignsSelected,
  isBlueprintsSelected,
  hasCampaignsFlip,
  toggleQueueExpanded,
  queueExpanded,
}) => {
  const { t } = useTranslation();

  const hasContentFeature = useAppSelector(selectHasContentFeature);

  const { isEnabled: isBlueprintsEnabled } = useSplitEnabled(
    'build-week-blueprints'
  );

  /*
   * Experiment 75 : We are going to test the performance of to put Create menu option on top,
   *  in the main menu versus to keep in the sidebar, only for new users at 50/50
   *
   * Segment A (Create option in the sidebar with magic wand)
   * Segment B (Create option in the main menu)
   */
  const { account } = useAccount();

  const currentOrganizationCreationDate =
    account?.currentOrganization?.createdAt;

  const epochsMs = currentOrganizationCreationDate
    ? new Date(currentOrganizationCreationDate).getTime()
    : 0;

  const { isReady, isTimedout, split } = useSplit(
    'EID75-create-mainmenu-vs-sidebar',
    { signUpDate: epochsMs }
  );

  const isOK = split && isReady && !isTimedout;

  const isEID75SegmentBEnabled = isOK && split.treatment === 'b';

  // is SegmentB is not enabled, then we have to check split EID74:
  // 50/50 for new users from 03/15/2023
  // Segment A is showing Content
  // Segment B is showing Cerate in sidebar

  const { config: eid74config, isEnabled: isEID74SegmentBEnabled } =
    useSplitEnabledWithConfig(
      'eid74-content-vs-create',
      { signUpDate: epochsMs },
      'b'
    );

  // hide option ony when segment B is active
  return (
    <ButtonWrapper>
      {!isEID75SegmentBEnabled && (
        <SidebarListItem
          id="content"
          title={
            eid74config && eid74config.title ? eid74config.title : 'Content'
          }
          onItemClick={onContentButtonClick}
          selected={isContentSelected}
          icon={<FoldersIcon />}
          badges={
            <BagdesWrapper>
              {isEID74SegmentBEnabled && <MagicWandIcon />}
              {!hasContentFeature && PaywallTag}
            </BagdesWrapper>
          }
        />
      )}
      <SidebarListItem
        id="calendar"
        title={t('calendar.common.title')}
        onItemClick={onCalendarButtonClick}
        selected={isCalendarSelected}
        icon={<CalendarIcon />}
      />

      <SidebarListItem
        id="campaigns"
        title={t('campaigns.common.title')}
        onItemClick={onCampaignsButtonClick}
        selected={isCampaignsSelected}
        badges={hasCampaignsFlip ? null : PaywallTag}
        icon={<ChartLineIcon />}
      />

      {isBlueprintsEnabled && (
        <SidebarListItem
          id="blueprints"
          title="Recipes"
          onItemClick={onBlueprintsButtonClick}
          selected={isBlueprintsSelected}
          badges={NewTag}
          icon={<CopyIcon />}
        />
      )}

      <Divider color="grayLight" marginTop="12px" marginBottom="12px" />

      <SidebarListItem
        id="queues"
        title={t('profile-sidebar.queues')}
        onItemClick={toggleQueueExpanded}
        selected={false}
        icon={queueExpanded ? <CaretUp /> : <CaretDown />}
      />
    </ButtonWrapper>
  );
};

SidebarButtons.propTypes = {
  queueExpanded: PropTypes.bool.isRequired,
  isContentSelected: PropTypes.bool.isRequired,
  isCalendarSelected: PropTypes.bool.isRequired,
  isCampaignsSelected: PropTypes.bool.isRequired,
  isBlueprintsSelected: PropTypes.bool.isRequired,
  hasCampaignsFlip: PropTypes.bool.isRequired,
  onContentButtonClick: PropTypes.func.isRequired,
  onCalendarButtonClick: PropTypes.func.isRequired,
  onCampaignsButtonClick: PropTypes.func.isRequired,
  onBlueprintsButtonClick: PropTypes.func.isRequired,
  toggleQueueExpanded: PropTypes.func.isRequired,
};

SidebarButtons.defaultProps = {};

export default SidebarButtons;
